/**
 * Staff-ui LawyerProfile form (create + edit), unified into a single
 * mode-aware component.
 *
 * Aligned with the canonical account-ui `LawyerProfileForm`
 * (`services/account-ui/src/pages/Profiles/LawyerProfileForm/LawyerProfileForm.tsx`)
 * via the shared staff-ui primitives in `@/components/Profiles` (ImageUploadField,
 * LanguageTabBar, useLanguageVersions, StatusPill, SaveActions,
 * FormValidationSummary, useFormValidation, Repeater). Status is driven by
 * which SaveActions button is pressed — there is no editable status field — and
 * Publish is gated behind `profiles.publish`.
 *
 * Staff-only additions (the CompanyPicker in create mode + the FirmChip in edit
 * mode) are retained because staff author across any firm, unlike the account
 * portal which derives the firm from the session.
 *
 * The publication-target picker (Website + Profile Language selects, with the
 * `(No offices)` suffix driven by the staff-bff cross-firm office-availability
 * endpoint) is implemented here per Issue 12 — no longer deferred.
 */
import {
  Button,
  ComboField,
  type ComboOption,
  Input,
  Select,
} from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  type ContentStatus,
  extractZodIssues,
  type ImageRef,
  type LawyerProfile,
  type LawyerProfileCreatePayload,
  type LawyerProfileGroupResponse,
  type LawyerProfileGroupVersion,
  type LawyerProfileUpdatePayload,
  lawyerProfileKeys,
  lawyerProfilesService,
  type Profession,
  type RegionAssignment,
  type SocialAccountType,
  type SocialProvider,
} from "@/api/profiles";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PageHeader } from "@/components/PageHeader";
import {
  FormValidationSummary,
  ImageUploadField,
  labelForLanguage,
  type LanguageVersion,
  LanguageTabBar,
  LANGUAGE_LABELS,
  Repeater,
  SaveActions,
  StatusPill,
  useFormValidation,
  useLanguageHotkeys,
  useLanguageVersions,
} from "@/components/Profiles";
import {
  AddressBookContactPicker,
  CompanyPicker,
  FirmChip,
} from "@/components/Profiles/ListFilters";
import { RegionsPicker } from "@/components/Profiles/RegionPicker";
import { RichTextField } from "@/components/Profiles/RichTextField";
import {
  LANGUAGE_LABELS as WEBSITE_LANGUAGE_LABELS,
  LANGUAGES,
  type Website,
  WEBSITE_OPTIONS,
} from "@/constants/features";
import { SPOKEN_LANGUAGE_OPTIONS } from "@/constants/spokenLanguages";
import { useOfficeAvailability } from "@/hooks/useOfficeAvailability";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesForm.module.css";
import { AuditPanel } from "../Audit/AuditPanel";

type StaffLawyerProfileFormProps =
  | { mode: "create"; initialCompanyId?: string }
  | { mode: "edit"; id: string };

type SocialMediaEntry = {
  provider: SocialProvider;
  accountType: SocialAccountType;
  url: string;
  sortOrder: number;
};

type LanguageSpokenEntry = {
  languageCode: string;
  sortOrder: number;
};

const PROFESSION_OPTIONS: ComboOption[] = [
  { value: "lawyer", label: "Lawyer" },
  { value: "barrister", label: "Barrister" },
  { value: "clerk", label: "Clerk" },
  { value: "team_member", label: "Team member" },
];

const SOCIAL_PROVIDER_OPTIONS: ComboOption[] = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "Twitter / X" },
  { value: "facebook", label: "Facebook" },
  { value: "web", label: "Website" },
];

const SOCIAL_ACCOUNT_TYPE_OPTIONS: ComboOption[] = [
  { value: "individual", label: "Individual" },
  { value: "company", label: "Company" },
];

// Spoken-languages ("Languages spoken" field) options. The stored value is the
// free-text language NAME (value === label). Sourced from the shared 50-name
// vocabulary so it stays identical to account-ui.
const LANGUAGE_OPTIONS: ComboOption[] = SPOKEN_LANGUAGE_OPTIONS;

// Set of valid spoken-language names, used by the draft schema to reject rows
// whose value isn't in the controlled vocabulary (e.g. a legacy WP blob like
// "Germany, Scottish & English").
const SPOKEN_LANGUAGE_VALUES = new Set(LANGUAGE_OPTIONS.map(o => o.value));

// Content-version languages for the LanguageTabBar (the en/fr/de translation
// tabs, driven by useLanguageVersions). This is the site/content language and
// is unrelated to the spoken-languages list above — do NOT expand it to the
// spoken-language vocabulary.
const ADD_LANGUAGE_CODES = ["en", "fr", "de", "zh"];

// Website (publishing target) + Profile-language options, mirroring
// StaffFirmProfileForm. Derived from the canonical WEBSITE_OPTIONS /
// LANGUAGES / LANGUAGE_LABELS so wire codes, labels and ordering stay in a
// single source of truth — never re-hardcode the list/order here.
const PROFILE_LANGUAGE_OPTIONS = LANGUAGES.map(code => ({
  value: code,
  label: WEBSITE_LANGUAGE_LABELS[code] ?? code,
}));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RICH_TEXT_MAX = 50_000;

type VersionPayload = {
  /** Server row id — present for persisted versions, absent for new/unsaved ones. */
  id?: string;
  contactId: string | null;
  firstName: string;
  lastName: string;
  title: string;
  position: string;
  workDepartment: string;
  telephone: string;
  email: string;
  career: string;
  education: string;
  personal: string;
  leisure: string;
  memberships: string;
  notableBarristers: string;
  currentBarristers: string;
  legal500Quotes: string;
  profession: Profession;
  photo: ImageRef | null;
  status: ContentStatus;
  socialMedia: SocialMediaEntry[];
  languagesSpoken: LanguageSpokenEntry[];
  regions: RegionAssignment[];
};

function emptyVersion(profession: Profession): VersionPayload {
  return {
    id: undefined,
    contactId: null,
    firstName: "",
    lastName: "",
    title: "",
    position: "",
    workDepartment: "",
    telephone: "",
    email: "",
    career: "",
    education: "",
    personal: "",
    leisure: "",
    memberships: "",
    notableBarristers: "",
    currentBarristers: "",
    legal500Quotes: "",
    profession,
    photo: null,
    status: "draft",
    socialMedia: [],
    languagesSpoken: [],
    regions: [],
  };
}

function fromApi(p: LawyerProfile): VersionPayload {
  return {
    id: p.id,
    contactId: p.contactId ?? null,
    firstName: p.firstName,
    lastName: p.lastName,
    title: p.title ?? "",
    position: p.position ?? "",
    workDepartment: p.workDepartment ?? "",
    telephone: p.telephone ?? "",
    email: p.email ?? "",
    career: p.career ?? "",
    education: p.education ?? "",
    personal: p.personal ?? "",
    leisure: p.leisure ?? "",
    memberships: p.memberships ?? "",
    notableBarristers: p.notableBarristers ?? "",
    currentBarristers: p.currentBarristers ?? "",
    legal500Quotes: p.legal500Quotes ?? "",
    profession: p.profession ?? "lawyer",
    photo: p.photoHash && p.photoFilename ? { hash: p.photoHash, filename: p.photoFilename } : null,
    status: p.status === "draft" || p.status === "published" ? p.status : "draft",
    socialMedia: (p.socialMedia ?? []).map(s => ({
      provider: s.provider,
      accountType: s.accountType,
      url: s.url,
      sortOrder: s.sortOrder,
    })),
    languagesSpoken: (p.languagesSpoken ?? []).map(l => ({
      languageCode: l.languageCode,
      sortOrder: l.sortOrder,
    })),
    // Country-only (§4.2): ignore any legacy `kind:"region"` rows on hydration.
    regions: (p.regions ?? [])
      .filter(r => r.kind === "country")
      .map(r => ({ kind: "country" as const, id: r.id })),
  };
}

const richTextField = () => z.string().max(RICH_TEXT_MAX).optional();

// Mirror of profiles-api `InsertLawyerProfileSchema`, trimmed to fields the UI
// collects today.
const LawyerProfileDraftSchema = z.object({
  contactId: z
    .string({ message: "You must link an Address Book contact" })
    .uuid({ message: "You must link an Address Book contact" })
    .nullable()
    .refine((v): v is string => v !== null, {
      message: "You must link an Address Book contact",
    }),
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  profession: z.enum(["lawyer", "barrister", "clerk", "team_member"]),
  website: z.string().optional(),
  email: z
    .string()
    .optional()
    .refine(v => !v || EMAIL_RE.test(v), "Email address is not valid"),
  career: richTextField(),
  education: richTextField(),
  memberships: richTextField(),
  personal: richTextField(),
  leisure: richTextField(),
  currentBarristers: richTextField(),
  notableBarristers: richTextField(),
  legal500Quotes: richTextField(),
  socialMedia: z.array(
    z.object({
      provider: z.enum(["linkedin", "twitter", "facebook", "web"]),
      accountType: z.enum(["individual", "company"]),
      url: z.url({ message: "Enter a valid URL" }),
      sortOrder: z.number().int().optional(),
    }),
  ),
  languagesSpoken: z.array(
    z.object({
      // Stored value is the free-text language NAME; it must be one of the
      // controlled-vocabulary options (LANGUAGE_OPTIONS / SPOKEN_LANGUAGE_VALUES).
      languageCode: z
        .string()
        .refine(v => SPOKEN_LANGUAGE_VALUES.has(v), "Select a valid language"),
      sortOrder: z.number().int().optional(),
    }),
  ),
});

type LawyerProfileFieldKey = Exclude<
  keyof z.infer<typeof LawyerProfileDraftSchema>,
  "socialMedia" | "languagesSpoken"
>;

const LAWYER_PROFILE_FIELD_KEYS = [
  "contactId",
  "firstName",
  "lastName",
  "profession",
  "website",
  "email",
  "career",
  "education",
  "memberships",
  "personal",
  "leisure",
  "currentBarristers",
  "notableBarristers",
  "legal500Quotes",
] as const satisfies ReadonlyArray<LawyerProfileFieldKey>;

const LAWYER_PROFILE_FIELD_LABELS: Record<LawyerProfileFieldKey, string> = {
  contactId: "Address Book contact",
  firstName: "First name",
  lastName: "Last name",
  profession: "Profession",
  website: "Website",
  email: "Email",
  career: "Career",
  education: "Education",
  memberships: "Memberships",
  personal: "Personal",
  leisure: "Leisure",
  currentBarristers: "Current barristers / practice areas",
  notableBarristers: "Notable barristers",
  legal500Quotes: "Legal 500 quotes",
};

const LAWYER_PROFILE_ARRAY_KEYS = ["socialMedia", "languagesSpoken"] as const;

const LAWYER_PROFILE_ARRAY_FIELD_LABELS = {
  socialMedia: {
    section: "Social media",
    subFields: { url: "URL", provider: "Provider", accountType: "Account type" },
  },
  languagesSpoken: {
    section: "Languages spoken",
    subFields: { languageCode: "Language" },
  },
};

// The per-version wire fields shared by the single create/update path and the
// batch group upsert. Excludes the identity fields (companyId / firstName /
// lastName / row id), which each save path supplies itself.
type SharedVersionFields = Omit<LawyerProfileCreatePayload, "companyId" | "firstName" | "lastName">;

type SingleSaveArgs = {
  firstName: string;
  lastName: string;
  shared: SharedVersionFields;
};

export function StaffLawyerProfileForm(props: StaffLawyerProfileFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = props.mode === "edit";
  const { hasPermission } = usePermissions();
  const canPublish = hasPermission("profiles.publish");

  const detailQuery = useQuery({
    queryKey: isEdit ? lawyerProfileKeys.detail(props.id) : ["__noop_lawyer_profile_detail__"],
    queryFn: () => lawyerProfilesService.get((props as { id: string }).id),
    enabled: isEdit,
  });

  // Build the initial `versions` array for the hook. Edit mode seeds from the
  // fetched record; create mode starts empty and `addLanguage("en")` on mount.
  const initialVersions = useMemo(() => {
    if (!isEdit) return [];
    if (!detailQuery.data) return [];
    const code = detailQuery.data.languageCode ?? "en";
    return [{ ...fromApi(detailQuery.data), languageCode: code }];
  }, [isEdit, detailQuery.data]);

  const {
    versions,
    activeCode,
    setActiveCode,
    activeVersion,
    patchActive,
    renameActiveLanguage,
    addLanguage,
    removeLanguage,
    markSaved,
    tabOptions,
  } = useLanguageVersions<VersionPayload>({
    initial: initialVersions,
    createEmpty: code => ({ ...emptyVersion("lawyer"), languageCode: code }) as VersionPayload,
    defaultActiveCode: "en",
  });

  useLanguageHotkeys({
    codes: tabOptions.map(o => o.code),
    activeCode,
    setActiveCode,
  });

  // Seed an English version in create-mode on first render.
  const seededRef = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (seededRef.current) return;
    if (versions.length === 0) {
      seededRef.current = true;
      addLanguage("en");
    }
  }, [isEdit, versions.length, addLanguage]);

  // Create-mode company lives in component state; edit-mode company comes from
  // the loaded record. Contacts are firm-scoped, so changing the company clears
  // the active version's contactId.
  const [companyId, setCompanyId] = useState<string | null>(
    props.mode === "create" ? (props.initialCompanyId ?? null) : null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // `website` is scalar (one publishing target per profile row), NOT
  // per-language-version, so it lives in component state rather than inside
  // VersionPayload. The content language is carried per-version by
  // `useLanguageVersions` (`languageCode`). Mirrors account-ui LawyerProfileForm.
  const [website, setWebsite] = useState<Website>("com_en");

  // Hydrate the scalar website from the loaded profile.
  useEffect(() => {
    if (isEdit && detailQuery.data) {
      setWebsite(detailQuery.data.website ?? "com_en");
    }
  }, [isEdit, detailQuery.data]);

  // Firm the profile belongs to — edit mode reads it from the loaded record,
  // create mode from the picked company. Drives both the address-book contact
  // scope and the cross-firm office-availability lookup below. Computed here
  // (before any early returns) so the hooks order stays stable.
  const contactCompanyId = isEdit ? (detailQuery.data?.companyId ?? null) : companyId;

  // Per-edition office availability for the lawyer's firm (cross-firm — the
  // profile's companyId). Options with no qualifying office get a
  // ` (No offices)` suffix + disabled state, matching the legacy WP dropdown.
  const officeAvailabilityQuery = useOfficeAvailability(contactCompanyId);
  const officeAvailability = officeAvailabilityQuery.data;

  const {
    errors: fieldErrors,
    arrayErrors,
    summary,
    orphanMessages,
    validate,
    clearError,
    clearArrayError,
    setServerIssues,
  } = useFormValidation({
    schema: LawyerProfileDraftSchema,
    labels: LAWYER_PROFILE_FIELD_LABELS,
    knownKeys: LAWYER_PROFILE_FIELD_KEYS,
    arrayKeys: LAWYER_PROFILE_ARRAY_KEYS,
    arrayFieldLabels: LAWYER_PROFILE_ARRAY_FIELD_LABELS,
  });

  // Shape one version's wire fields. Shared by the single create/update path and
  // the batch group upsert so the two never drift. `languageCode` is passed in
  // (the single path may honour a website lock; the group path uses each
  // version's own code). `website` is scalar and captured from state.
  const buildVersionPayload = (
    candidate: LanguageVersion<VersionPayload>,
    targetStatus: ContentStatus,
    languageCode: string,
  ): SharedVersionFields => ({
    contactId: candidate.contactId as string,
    photoHash: candidate.photo?.hash ?? null,
    photoFilename: candidate.photo?.filename ?? null,
    languageCode: languageCode || undefined,
    website,
    profession: candidate.profession,
    title: candidate.title || undefined,
    telephone: candidate.telephone || undefined,
    email: candidate.email || undefined,
    workDepartment: candidate.workDepartment || undefined,
    position: candidate.position || undefined,
    career: candidate.career || undefined,
    education: candidate.education || undefined,
    personal: candidate.personal || undefined,
    leisure: candidate.leisure || undefined,
    memberships: candidate.memberships || undefined,
    // Clerk-only rich-text fields — only include when the profession is "clerk",
    // otherwise we'd inadvertently blank server-side data.
    ...(candidate.profession === "clerk"
      ? {
          notableBarristers: candidate.notableBarristers || undefined,
          currentBarristers: candidate.currentBarristers || undefined,
          legal500Quotes: candidate.legal500Quotes || undefined,
        }
      : {}),
    status: targetStatus,
    socialMedia: candidate.socialMedia.map((s, idx) => ({
      provider: s.provider,
      accountType: s.accountType,
      url: s.url,
      sortOrder: s.sortOrder ?? idx,
    })),
    languagesSpoken: candidate.languagesSpoken.map((l, idx) => ({
      languageCode: l.languageCode,
      sortOrder: l.sortOrder ?? idx,
    })),
    regions: candidate.regions.map(r => ({ kind: r.kind, id: r.id })),
  });

  const saveMutation = useMutation({
    mutationFn: async ({ firstName, lastName, shared }: SingleSaveArgs): Promise<LawyerProfile> => {
      if (isEdit) {
        const patch: LawyerProfileUpdatePayload = { firstName, lastName, ...shared };
        return lawyerProfilesService.update((props as { id: string }).id, patch);
      }
      if (!companyId) {
        throw new Error("Company is required to create a lawyer profile");
      }
      const input: LawyerProfileCreatePayload = { companyId, firstName, lastName, ...shared };
      return lawyerProfilesService.create(input);
    },
    onSuccess: async saved => {
      if (activeVersion) {
        markSaved([{ ...activeVersion, status: saved.status }]);
      }
      await queryClient.invalidateQueries({ queryKey: lawyerProfileKeys.all });
      navigate(ROUTES.profiles.lawyerProfiles);
    },
    onError: (err: Error) => {
      const issues = extractZodIssues(err);
      if (issues && issues.length > 0) {
        setServerIssues(issues);
        setFormError(null);
        return;
      }
      setFormError(err.message);
    },
  });

  const groupMutation = useMutation({
    mutationFn: (targetStatus: ContentStatus): Promise<LawyerProfileGroupResponse> => {
      const resolvedCompanyId = isEdit
        ? (detailQuery.data?.companyId ?? undefined)
        : (companyId ?? undefined);
      const groupId = isEdit
        ? (detailQuery.data?.profileGroupId ?? crypto.randomUUID())
        : crypto.randomUUID();
      const groupVersions: LawyerProfileGroupVersion[] = versions.map(candidate => ({
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        ...buildVersionPayload(candidate, targetStatus, candidate.languageCode),
        ...(candidate.id ? { id: candidate.id } : {}),
      }));
      return lawyerProfilesService.batchUpsertGroup(groupId, {
        companyId: resolvedCompanyId,
        versions: groupVersions,
      });
    },
    onSuccess: async response => {
      markSaved(
        response.lawyerProfiles.map(saved => ({
          ...fromApi(saved),
          languageCode: saved.languageCode ?? "en",
        })),
      );
      await queryClient.invalidateQueries({ queryKey: lawyerProfileKeys.all });
      navigate(ROUTES.profiles.lawyerProfiles);
    },
    onError: (err: Error) => {
      const issues = extractZodIssues(err);
      if (issues && issues.length > 0) {
        setServerIssues(issues);
        setFormError(null);
        return;
      }
      setFormError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => lawyerProfilesService.remove((props as { id: string }).id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: lawyerProfileKeys.all });
      navigate(ROUTES.profiles.lawyerProfiles);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  if (isEdit && detailQuery.isPending) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Lawyer profile" />
        <p role="status" className={styles.muted}>
          Loading…
        </p>
      </div>
    );
  }
  if (isEdit && detailQuery.isError) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Lawyer profile" />
        <p role="alert" className={styles.errorBanner}>
          {detailQuery.error instanceof Error
            ? detailQuery.error.message
            : "Failed to load lawyer profile"}
        </p>
      </div>
    );
  }

  if (!activeVersion) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={isEdit ? "Lawyer profile" : "New lawyer profile"} />
        <p role="status" className={styles.muted}>
          Loading…
        </p>
      </div>
    );
  }

  // `activeVersion` is now defined — alias for terser JSX.
  const version = activeVersion;

  // Derive the Profile Language lock from the canonical WEBSITE_OPTIONS each
  // render so the UI can never drift from the server's allowed-language rule.
  // `allowedLanguages` is null for com_en/com_zh (free choice) and a single-code
  // array for fr/de (locked to that code).
  const selectedWebsite = WEBSITE_OPTIONS.find(o => o.value === website);
  const allowedLanguages = selectedWebsite?.allowedLanguages ?? null;
  const languageLocked = allowedLanguages !== null && allowedLanguages.length === 1;
  const lockedLanguage = languageLocked ? allowedLanguages[0] : null;

  const handleWebsiteChange = (value: string) => {
    const next = value as Website;
    const opt = WEBSITE_OPTIONS.find(o => o.value === next);
    setWebsite(next);
    // When switching to a locked edition (fr/de), force the active version's
    // content language to the single allowed code so the saved combo is always
    // server-valid.
    if (opt?.allowedLanguages) {
      renameActiveLanguage(opt.allowedLanguages[0]);
    }
    clearError("website");
  };

  const websiteOptions = WEBSITE_OPTIONS.map(({ value, label }) => {
    // Until availability is known, treat every edition as available.
    const available = officeAvailability ? officeAvailability[value] : true;
    // Keep a currently-saved-but-now-unavailable value selectable so it
    // round-trips rather than silently resetting.
    const isSaved = value === website;
    return {
      value,
      label: available ? label : `${label} (No offices)`,
      disabled: !available && !isSaved,
    };
  });

  const handleSave = (targetStatus: ContentStatus) => {
    setFormError(null);
    setRegionsError(null);

    if (!isEdit && !companyId) {
      setFormError("Company is required");
      return;
    }

    const parsed = validate({
      contactId: version.contactId,
      firstName: version.firstName,
      lastName: version.lastName,
      profession: version.profession,
      website,
      email: version.email || undefined,
      career: version.career || undefined,
      education: version.education || undefined,
      memberships: version.memberships || undefined,
      personal: version.personal || undefined,
      leisure: version.leisure || undefined,
      currentBarristers: version.currentBarristers || undefined,
      notableBarristers: version.notableBarristers || undefined,
      legal500Quotes: version.legal500Quotes || undefined,
      socialMedia: version.socialMedia,
      languagesSpoken: version.languagesSpoken,
    });

    if (parsed === null) {
      return;
    }

    if (version.regions.length === 0) {
      setRegionsError("At least one region is required");
      return;
    }

    // Single-language profile: keep the existing create/update path (preserves
    // the staff-bff company guard on POST / PUT :id). The persisted content
    // language honours a website lock.
    if (versions.length === 1) {
      const languageCode = lockedLanguage ?? activeCode;
      saveMutation.mutate({
        firstName: version.firstName,
        lastName: version.lastName,
        shared: buildVersionPayload(version, targetStatus, languageCode),
      });
      return;
    }

    // Multi-language group save: every version must be independently complete —
    // profiles-api rejects a group version missing a contact, name or region.
    for (const candidate of versions) {
      if (
        !candidate.contactId ||
        candidate.firstName.trim() === "" ||
        candidate.lastName.trim() === "" ||
        candidate.regions.length === 0
      ) {
        setFormError(
          `Language "${labelForLanguage(candidate.languageCode)}" is missing a contact, name, or region`,
        );
        return;
      }
    }

    groupMutation.mutate(targetStatus);
  };

  const currentStatus: ContentStatus =
    version.status === "draft" || version.status === "published" ? version.status : "draft";

  const availableLanguageCodes = ADD_LANGUAGE_CODES.filter(
    code => !versions.some(v => v.languageCode === code),
  );
  const canRemoveLanguage = versions.length > 1;
  const fullName = `${version.firstName} ${version.lastName}`.trim();

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={isEdit ? `Edit lawyer profile: ${fullName}` : "New lawyer profile"}
        meta={
          isEdit && detailQuery.data ? (
            <FirmChip companyId={detailQuery.data.companyId} />
          ) : undefined
        }
      >
        <div className={styles.headerActions}>
          <Button variant="outlined" onClick={() => navigate(ROUTES.profiles.lawyerProfiles)}>
            Cancel
          </Button>
          <SaveActions
            layout="inline"
            currentStatus={currentStatus}
            canPublish={canPublish}
            isSaving={saveMutation.isPending || groupMutation.isPending}
            onSave={handleSave}
            onDelete={isEdit ? () => setDeleteOpen(true) : undefined}
          />
        </div>
      </PageHeader>

      <FormValidationSummary entries={summary} />

      {(formError || orphanMessages.length > 0) && (
        <div role="alert" className={styles.errorBanner}>
          {formError ?? orphanMessages.join("\n")}
        </div>
      )}

      <LanguageTabBar
        languages={tabOptions}
        activeCode={activeCode}
        onChange={setActiveCode}
        availableLanguages={availableLanguageCodes.map(code => ({
          code,
          label: LANGUAGE_LABELS[code] ?? code.toUpperCase(),
        }))}
        onAdd={addLanguage}
        onRemove={canRemoveLanguage ? removeLanguage : undefined}
        addLabel="Add language"
      />

      <div className={styles.layout}>
        <div className={styles.main}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Publication</h2>
            <div className={styles.fieldGrid}>
              <Select
                label="Website"
                value={website}
                options={websiteOptions}
                error={fieldErrors.website}
                onChange={e => handleWebsiteChange(e.target.value)}
                fullWidth
              />
              <Select
                label="Profile language"
                value={lockedLanguage ?? version.languageCode}
                options={PROFILE_LANGUAGE_OPTIONS}
                disabled={languageLocked}
                onChange={e => {
                  const code = e.target.value;
                  // Switching a mono-lingual profile's language renames the
                  // active version (no stray version); if the code is an
                  // existing sibling version, just switch to it.
                  if (versions.some(v => v.languageCode === code)) {
                    setActiveCode(code);
                  } else {
                    renameActiveLanguage(code);
                  }
                }}
                fullWidth
              />
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Personal</h2>
            <div className={styles.fieldGrid}>
              <Input
                label="First name"
                fullWidth
                required
                aria-required="true"
                error={fieldErrors.firstName}
                value={version.firstName}
                onChange={e => {
                  patchActive({ firstName: e.target.value });
                  clearError("firstName");
                }}
              />
              <Input
                label="Last name"
                fullWidth
                required
                aria-required="true"
                error={fieldErrors.lastName}
                value={version.lastName}
                onChange={e => {
                  patchActive({ lastName: e.target.value });
                  clearError("lastName");
                }}
              />
              <Input
                label="Title"
                fullWidth
                value={version.title}
                onChange={e => patchActive({ title: e.target.value })}
              />
              <ComboField
                label="Profession"
                fullWidth
                options={PROFESSION_OPTIONS}
                value={version.profession}
                onChange={value => patchActive({ profession: value as Profession })}
              />
            </div>

            <ImageUploadField
              label="Profile photo"
              value={version.photo}
              onChange={photo => patchActive({ photo })}
              slug={fullName || undefined}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Position / role</h2>
            <RichTextField
              label="Position / role"
              preset="basic_enhanced"
              mediaEnabled={false}
              value={version.position}
              onChange={html => patchActive({ position: html })}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Contact</h2>
            <div className={styles.fieldGrid}>
              <Input
                label="Telephone"
                fullWidth
                value={version.telephone}
                onChange={e => patchActive({ telephone: e.target.value })}
              />
              <Input
                type="email"
                label="Email"
                fullWidth
                error={fieldErrors.email}
                value={version.email}
                onChange={e => {
                  patchActive({ email: e.target.value });
                  clearError("email");
                }}
              />
              <Input
                label="Work department"
                fullWidth
                value={version.workDepartment}
                onChange={e => patchActive({ workDepartment: e.target.value })}
              />
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Career</h2>
            <RichTextField
              label="Career"
              preset="basic_enhanced"
              mediaEnabled={false}
              value={version.career}
              onChange={html => patchActive({ career: html })}
            />
            {version.profession === "clerk" && (
              <>
                <RichTextField
                  label="Current barristers / practice areas"
                  preset="full"
                  mediaEnabled
                  value={version.currentBarristers}
                  onChange={html => patchActive({ currentBarristers: html })}
                />
                <RichTextField
                  label="Notable barristers"
                  preset="full"
                  mediaEnabled
                  value={version.notableBarristers}
                  onChange={html => patchActive({ notableBarristers: html })}
                />
              </>
            )}
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Education</h2>
            <RichTextField
              label="Education"
              preset="basic_enhanced"
              mediaEnabled={false}
              value={version.education}
              onChange={html => patchActive({ education: html })}
            />
            <RichTextField
              label="Memberships"
              preset="basic_enhanced"
              mediaEnabled={false}
              value={version.memberships}
              onChange={html => patchActive({ memberships: html })}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Personal notes</h2>
            <RichTextField
              label="Personal"
              preset="basic_enhanced"
              mediaEnabled={false}
              value={version.personal}
              onChange={html => patchActive({ personal: html })}
            />
            <RichTextField
              label="Leisure"
              preset="basic_enhanced"
              mediaEnabled={false}
              value={version.leisure}
              onChange={html => patchActive({ leisure: html })}
            />
            {version.profession === "clerk" && (
              <RichTextField
                label="Legal 500 quotes"
                preset="full"
                mediaEnabled
                value={version.legal500Quotes}
                onChange={html => patchActive({ legal500Quotes: html })}
              />
            )}
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Social media</h2>
            <Repeater<SocialMediaEntry>
              items={version.socialMedia}
              onChange={next => patchActive({ socialMedia: next })}
              createEmpty={() => ({
                provider: "linkedin",
                accountType: "individual",
                url: "",
                sortOrder: version.socialMedia.length,
              })}
              addLabel="Add social link"
              emptyState="No social links yet."
              rowErrors={arrayErrors.socialMedia}
              renderRow={(item, index, update, errs) => (
                <>
                  <ComboField
                    label="Provider"
                    fullWidth
                    options={SOCIAL_PROVIDER_OPTIONS}
                    value={item.provider}
                    onChange={value => {
                      update({ provider: value as SocialMediaEntry["provider"] });
                      clearArrayError("socialMedia", index, "provider");
                    }}
                  />
                  <ComboField
                    label="Account type"
                    fullWidth
                    options={SOCIAL_ACCOUNT_TYPE_OPTIONS}
                    value={item.accountType}
                    onChange={value => {
                      update({ accountType: value as SocialMediaEntry["accountType"] });
                      clearArrayError("socialMedia", index, "accountType");
                    }}
                  />
                  <Input
                    label="URL"
                    fullWidth
                    error={errs?.url}
                    value={item.url}
                    onChange={e => {
                      update({ url: e.target.value });
                      clearArrayError("socialMedia", index, "url");
                    }}
                  />
                </>
              )}
            />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Languages spoken</h2>
            <Repeater<LanguageSpokenEntry>
              items={version.languagesSpoken}
              onChange={next => patchActive({ languagesSpoken: next })}
              createEmpty={() => ({
                languageCode: "English",
                sortOrder: version.languagesSpoken.length,
              })}
              addLabel="Add language"
              emptyState="No languages yet."
              rowErrors={arrayErrors.languagesSpoken}
              renderRow={(item, index, update, errs) => (
                <ComboField
                  label="Language"
                  fullWidth
                  options={LANGUAGE_OPTIONS}
                  error={errs?.languageCode}
                  value={item.languageCode}
                  onChange={value => {
                    update({ languageCode: value });
                    clearArrayError("languagesSpoken", index, "languageCode");
                  }}
                />
              )}
            />
          </section>
        </div>

        <aside className={styles.sidebar}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Status</h2>
            <StatusPill status={currentStatus} />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Countries</h2>
            <RegionsPicker
              label="Countries (at least one required)"
              // Scope to the countries the selected Website publishes (spec 19).
              site={selectedWebsite?.domain}
              value={version.regions}
              onChange={regions => {
                patchActive({ regions });
                if (regions.length > 0) setRegionsError(null);
              }}
              error={regionsError}
            />
          </section>

          {!isEdit && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Company</h2>
              <CompanyPicker
                value={companyId}
                onChange={id => {
                  setCompanyId(id);
                  // Contacts are firm-scoped — clear any prior selection so a
                  // stale cross-firm contact can't be submitted.
                  patchActive({ contactId: null });
                }}
              />
              <p className={styles.labelHint}>Required: select the firm this profile belongs to.</p>
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Address book link</h2>
            <AddressBookContactPicker
              companyId={contactCompanyId}
              value={version.contactId}
              required
              onChange={id => {
                patchActive({ contactId: id });
                clearError("contactId");
              }}
              error={fieldErrors.contactId}
            />
          </section>

          {props.mode === "edit" && <AuditPanel entityType="lawyer_profile" entityId={props.id} />}
        </aside>
      </div>

      {deleteOpen && isEdit && (
        <ConfirmationModal
          title={`Delete lawyer profile "${fullName}"?`}
          message="This will permanently delete the lawyer profile."
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setDeleteOpen(false)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync();
          }}
        />
      )}
    </div>
  );
}
